import { DocsBreadcrumb } from '@/business-components/docs-breadcrumb/DocsBreadcrumb';
import { NavbarGroup } from '@/business-components/navbar-group/NavbarGroup';
import SidebarSearch from '@/business-components/sidebar-tree/components/sidebar-search';
import { SidebarTree } from '@/business-components/sidebar-tree/SidebarTree';
import usePersistFn from '@/hooks/usePersistFn';
import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useNavigation } from '@remix-run/react';
import { Button, Drawer } from 'aui';
import { FoxIconMenu01 } from 'aui-biz-icons';
import clsx from 'clsx';
import React from 'react';
import { BrandLogoArea } from '../../../brand-logo-area/BrandLogoArea';
import type { useLevelNavbarGroup } from '../../hooks/useLevelNavbarGroup';
import styles from './MoreLevelNavbarContent.module.css';

export type MoreLevelNavbarContentProps = {
  secondLevelNavbarGroup: ReturnType<typeof useLevelNavbarGroup>['secondLevelNavbarGroup'];
  thirdLevelNavbarGroup: ReturnType<typeof useLevelNavbarGroup>['thirdLevelNavbarGroup'];
  className?: string;
};

export function MoreLevelNavbarContent(props: MoreLevelNavbarContentProps) {
  const { className, secondLevelNavbarGroup, thirdLevelNavbarGroup } = props;

  const isOpenDebugPanel = useAppearanceConfigModelStore((x) => x.isOpenDebugPanel);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const hiddenDrawer = usePersistFn(() => {
    setOpenDrawer(false);
  });

  const navigation = useNavigation();
  React.useEffect(() => {
    if (navigation.state === 'idle') {
      hiddenDrawer();
    }
  }, [navigation.state, hiddenDrawer]);

  const hideSearchBarInTree = Boolean(secondLevelNavbarGroup || thirdLevelNavbarGroup);

  return (
    <div
      className={clsx(
        '_navbar-level-2 flex border-b border-b-[#0000000a] dark:border-b-[#ffffff0f] os:justify-center os:py-1.5',
        {
          ['os:hidden']: !secondLevelNavbarGroup && !thirdLevelNavbarGroup,
        },
        className,
      )}
    >
      {(secondLevelNavbarGroup || thirdLevelNavbarGroup) && (
        <div
          className={clsx('hidden h-9 items-center px-5 os:flex os:w-0 os:flex-1 os:px-8', {
            ['os:max-w-[1560px]']: !isOpenDebugPanel,
            ['os:max-w-[1800px]']: isOpenDebugPanel,
          })}
        >
          {secondLevelNavbarGroup && (
            <NavbarGroup
              {...secondLevelNavbarGroup}
              containerClassName={clsx({
                ['os:flex-1 os:w-0']: !thirdLevelNavbarGroup,
              })}
              onClick={hiddenDrawer}
            />
          )}
          {secondLevelNavbarGroup && thirdLevelNavbarGroup && (
            <div className="mx-3 hidden h-3 border-l os:block" />
          )}
          {thirdLevelNavbarGroup && (
            <NavbarGroup
              {...thirdLevelNavbarGroup}
              containerClassName="flex-1 w-0"
              onClick={hiddenDrawer}
            />
          )}
        </div>
      )}

      <div className="flex items-center gap-2 px-3 py-2 os:hidden">
        <Button
          aria-label="open sidebar drawer"
          type="text"
          icon={<FoxIconMenu01 />}
          onClick={() => setOpenDrawer(true)}
        />

        <DocsBreadcrumb />
      </div>

      <Drawer
        className={clsx('os:hidden', styles.drawer)}
        placement="left"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        title={<BrandLogoArea onlyShowBrand onVersionSelectClick={hiddenDrawer} />}
      >
        {hideSearchBarInTree && (
          <div className="mb-3 mt-2 pr-2 os:pr-5">
            <SidebarSearch />
          </div>
        )}
        {secondLevelNavbarGroup && (
          <NavbarGroup
            {...secondLevelNavbarGroup}
            containerClassName="mb-2.5 pr-2"
            onClick={hiddenDrawer}
          />
        )}
        {thirdLevelNavbarGroup && (
          <NavbarGroup
            {...thirdLevelNavbarGroup}
            containerClassName="pr-2"
            onClick={hiddenDrawer}
          />
        )}

        {(secondLevelNavbarGroup || thirdLevelNavbarGroup) && (
          <div className="mt-5 border-b border-solid border-color-split" />
        )}

        <SidebarTree hideSearchBarInTree={hideSearchBarInTree} />
      </Drawer>
    </div>
  );
}
